import { useEffect, useMemo } from "react"
import useCourses from "../../hooks/use-courses"
import { useFormStore } from "../../stores/use-form-store"
import {
	ProgramFieldConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { SelectField } from "./select"
import { parseAttributes } from "@/lib/utils"

export const ProgramField = ({ form, field, commonAttributes }: FieldProps) => {
	const {
		config,
		submissionValues,
		applicationId,
		applicationSubmission,
		verifiedFields,
	} = useFormStore()
	const { ProgramTypeFieldId } = getProgramFieldAttributes(field.attributes)
	const selectedProgramType =
		form.watch(ProgramTypeFieldId!) ||
		applicationSubmission[ProgramTypeFieldId!]

	const { useGetPrograms } = useCourses()
	const { data: programs } = useGetPrograms({
		universityId: config?.university_id ?? "",
		batchId: config?.batch_id ?? "",
		applicationId: applicationId ?? "",
		programTypeId: selectedProgramType || "program_type_id",
		type: config?.type ?? "FORM",
		widgetId: config?.widget_id ?? "",
	})

	const options = useMemo(() => {
		if (!programs) return []

		return programs.map(program => ({
			label: program.label,
			value: program.value,
		}))
	}, [programs])

	useEffect(() => {
		if (!selectedProgramType || !options.length) return
		if (submissionValues && submissionValues[field.field_id]) {
			const value = submissionValues[field.field_id]
			if (value != "") {
				form.setValue(field.field_id, value)
			}
		}
	}, [options, submissionValues, selectedProgramType])

	return (
		<SelectField
			form={form}
			field={field}
			commonAttributes={commonAttributes}
			optionsPreSet={options}
			isDisabled={!programs || verifiedFields.has(field.field_id)}
		/>
	)
}

function getProgramFieldAttributes(attributes: Attribute[]) {
	const props: {
		ProgramTypeFieldId?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case ProgramFieldConfig.ProgramTypeFieldId:
				props.ProgramTypeFieldId = value as string
		}
	})

	return props
}
